import { z } from "zod";
import {
    AdminQueueActionsSchema,
    AdminQueueClientSchema,
    LeadStatusEnum,
    MatchingStrategyEnum,
    UrgencyEnum,
    StoreSchema,
    LeadClientSchema,
    ExpertMinimalSchema,
    ExpertWithEmailSchema,
    LeadMatchClientSchema,
    LeadMatchAdminSchema,
    LeadMatchExpertSchema,
} from "./common";
import {
    ProjectParticipantGroupEnum,
    ProjectParticipantRoleEnum,
    ProjectTypeEnum,
} from "../projects-v2/common";

// ==================
// Lead Response Schemas (different for each role)
// ==================

/** Lead response for Client view */
export const LeadClientResponseSchema = z.object({
    id: z.number().int().positive(),
    project_title: z.string(),
    project_brief: z.string().nullable(),
    urgency: UrgencyEnum,
    matching_strategy: MatchingStrategyEnum,
    status: LeadStatusEnum,
    budget_range: z.string(),
    created_at: z.string(),
    matched_at: z.string().nullable(),
    selected_expert: ExpertMinimalSchema.nullable(),
    matches: z.array(LeadMatchClientSchema),
    matches_count: z.number().int().min(0),
    store: StoreSchema,
});

export type LeadClientResponse = z.infer<typeof LeadClientResponseSchema>;

/** Lead response for Admin view (includes client info, emails, matching limits) */
export const LeadAdminResponseSchema = z.object({
    id: z.number().int().positive(),
    project_title: z.string(),
    project_brief: z.string().nullable(),
    urgency: UrgencyEnum,
    matching_strategy: MatchingStrategyEnum,
    status: LeadStatusEnum,
    timezone: z.string().nullable().optional(),
    budget_range: z.string(),
    created_at: z.string(),
    matched_at: z.string().nullable(),
    client: LeadClientSchema,
    selected_expert: ExpertWithEmailSchema.nullable(),
    matches: z.array(LeadMatchAdminSchema),
    matches_count: z.number().int().min(0),
    max_matches: z.number().int().positive(),
    can_match_more: z.boolean(),
    store: StoreSchema,
});

export type LeadAdminResponse = z.infer<typeof LeadAdminResponseSchema>;

/** Lead response for Expert view (includes client info, only their own match) */
export const LeadExpertResponseSchema = z.object({
    id: z.number().int().positive(),
    project_title: z.string(),
    project_brief: z.string().nullable(),
    urgency: UrgencyEnum,
    matching_strategy: MatchingStrategyEnum,
    status: LeadStatusEnum,
    budget_range: z.string(),
    created_at: z.string(),
    matched_at: z.string().nullable(),
    client: LeadClientSchema,
    store: StoreSchema,
    match: LeadMatchExpertSchema,
});

export type LeadExpertResponse = z.infer<typeof LeadExpertResponseSchema>;

// ==================
// Action Response Schemas
// ==================

/** Response after matching experts to a lead (Admin) */
export const MatchExpertsResponseSchema = z.object({
    id: z.number().int().positive(),
    project_title: z.string(),
    status: LeadStatusEnum,
    matches_count: z.number().int().min(0),
    max_matches: z.number().int().positive(),
    can_match_more: z.boolean(),
    matches: z.array(LeadMatchAdminSchema),
});

export type MatchExpertsResponse = z.infer<typeof MatchExpertsResponseSchema>;

export const AdminQueueProjectParticipantSchema = z.object({
    id: z.number(),
    org_id: z.number(),
    participant_group: ProjectParticipantGroupEnum,
    participant_role: ProjectParticipantRoleEnum,
    organization: z.object({
        id: z.number().nullish(),
        name: z.string().nullish(),
        org_type: z.string().nullish(),
    }),
});
export type AdminQueueProjectParticipant = z.infer<typeof AdminQueueProjectParticipantSchema>;

export const AdminQueueProjectSchema = z.object({
    id: z.number(),
    lead_match_id: z.number().nullable(),
    project_type: ProjectTypeEnum.default("standard"),
    title: z.string(),
    description: z.string().nullable(),
    project_status: z.string(),
    status_changed_at: z.string().nullable(),
    created_at: z.string().nullable(),
    match_type: z.string().nullable(),
    participants: z.array(AdminQueueProjectParticipantSchema),
});
export type AdminQueueProject = z.infer<typeof AdminQueueProjectSchema>;

export const AdminQueueQuoteSummarySchema = z.object({
    id: z.number(),
    status: z.string(),
    payment_status: z.string().nullable(),
    amount_cents: z.number(),
    created_at: z.string().nullable(),
    status_changed_at: z.string().nullable(),
});
export type AdminQueueQuoteSummary = z.infer<typeof AdminQueueQuoteSummarySchema>;

export const AdminQueueLeadSummarySchema = z.object({
    id: z.number(),
    project_title: z.string(),
    project_brief: z.string().nullable(),
    urgency: UrgencyEnum,
    matching_strategy: MatchingStrategyEnum,
    status: LeadStatusEnum,
    created_at: z.string(),
    matched_at: z.string().nullable(),
    budget_range: z.string(),
});
export type AdminQueueLeadSummary = z.infer<typeof AdminQueueLeadSummarySchema>;

export const AdminQueueAttentionSchema = z.object({
    reason: z.enum(["client", "expert"]).default("client"),
    waiting_since: z.string(),
    waiting_days: z.number().int(),
    last_alerted_at: z.string().nullable(),
});
export type AdminQueueAttention = z.infer<typeof AdminQueueAttentionSchema>;

export const AdminLeadQueueRowSchema = z.object({
    match_id: z.number().optional(),
    lead: AdminQueueLeadSummarySchema,
    client: AdminQueueClientSchema,
    store: StoreSchema,
    selected_expert: ExpertWithEmailSchema.nullable(),
    matched_expert: ExpertWithEmailSchema.nullable(),
    project: AdminQueueProjectSchema.nullable(),
    latest_quote: AdminQueueQuoteSummarySchema.nullable(),
    admin_actions: AdminQueueActionsSchema,
    attention: AdminQueueAttentionSchema.nullish(),
});
export type AdminLeadQueueRow = z.infer<typeof AdminLeadQueueRowSchema>;
